import { Client } from '@openfeature/web-sdk';
import { modules } from 'src/constants';
import { Module } from 'src/initialization/renderConditions';

export const Flags = {
	qseKillSwitch: 'qse-kill-switch',
	accountingKillSwitch: 'digit-kill-switch',
	qseRenderByFlags: 'qse-use-flags-to-render',
	EnableSmartLook: 'enable-smartlook',
	CookieAuthProcess: 'cookie-auth-process',
	EnableEventBus: 'enable-eventbus',
} as const;

export type FeatureDecisionsReturnType = {
	qseShouldRender: (module?: Module) => boolean;
	accountingShouldRender: (module?: Module) => boolean;
	trackingShouldRender: (module?: Module) => boolean;
	chatShouldRender: (module?: Module) => boolean;
	notificationBannerShouldRender: (module?: Module) => boolean;
	toastContainerShouldRender: (module?: Module) => boolean;
	translationsEditorShouldRender: (module?: Module) => boolean;
	machineKeysShouldRender: (module?: Module) => boolean;
	usersShouldRender: (module?: Module) => boolean;
	permissionsShouldRender: (module?: Module) => boolean;
	emailShouldRender: (module?: Module) => boolean;
	cookiesBarShouldRender: (module?: Module) => boolean;
	importMappingShouldRender: (module?: Module) => boolean;
	fileLookUpsShouldRender: (module?: Module) => boolean;
	emailTemplatesShouldRender: (module?: Module) => boolean;
	qseShouldUseTestMode: () => boolean;
	isSmartlookEnabled: () => boolean;
	enableEventBus: () => boolean;
};

export default function createFeatureDecisions(
	features: Client,
	permissions?: any,
): FeatureDecisionsReturnType {
	return {
		enableEventBus() {
			return features.getBooleanValue(Flags.EnableEventBus, false);
		},
		isSmartlookEnabled() {
			return features.getBooleanValue(Flags.EnableSmartLook, false);
		},
		qseShouldUseTestMode() {
			return features.getBooleanValue(Flags.qseRenderByFlags, true);
		},

		qseShouldRender(module) {
			const isCorrectUrl =
				window.location.href
					.toLowerCase()
					.indexOf('schemeserve/admin/questions/edit.aspx') !== -1;

			const { search } = window.location;
			const params = new URLSearchParams(search);

			if (features.getBooleanValue(Flags.qseRenderByFlags, true)) {
				const isTestMode = params.get('testMode') === 'true';
				return (
					window.isFrontendEnabled &&
					isCorrectUrl &&
					isTestMode &&
					(window.isLoggedIn || !!module?.isPreLogin)
				);
			}

			const hasContainer = !!window.document.getElementById(
				'questions-editor-react-content',
			);

			const oldUi = params.get('oldUi') === 'true';

			if (oldUi) return false;
			const flag = features.getBooleanValue(Flags.qseKillSwitch, false);

			return (
				window.isFrontendEnabled &&
				(window.isLoggedIn || !!module?.isPreLogin) &&
				(isCorrectUrl || process.env.NODE_ENV === 'development') &&
				flag &&
				permissions[modules.QUESTIONS_EDITOR]
			);
		},

		accountingShouldRender(module) {
			const hasContainer = !!window.document.getElementById(
				'accounting-react-content',
			);
			const isCorrectUrl =
				window.location.href.toLowerCase().indexOf('schemeserve/accounting') !==
				-1;

			return (
				window.isFrontendEnabled &&
				(window.isLoggedIn || !!module?.isPreLogin) &&
				// hasContainer &&
				isCorrectUrl &&
				features.getBooleanValue(Flags.accountingKillSwitch, false)
			);
		},

		trackingShouldRender(module) {
			return window.isLoggedIn || !!module?.isPreLogin || true;
		},

		chatShouldRender(module) {
			return window.isLoggedIn || !!module?.isPreLogin || true;
		},

		notificationBannerShouldRender(module) {
			return window.isLoggedIn || !!module?.isPreLogin || true;
		},

		toastContainerShouldRender(module) {
			const tmp = window.isLoggedIn || !!module?.isPreLogin || true;
			console.log('tmp', tmp);
			return tmp;
		},

		translationsEditorShouldRender(module) {
			const isADMTranslationsUrl =
				window.location.href.toLowerCase().indexOf('adm/translations.aspx') !==
				-1;

			const isStandardTranslationsUrl =
				window.location.href
					.toLowerCase()
					.indexOf('/settings/translations.aspx') !== -1;

			return (
				window.isFrontendEnabled &&
				(window.isLoggedIn || !!module?.isPreLogin) &&
				(isADMTranslationsUrl || isStandardTranslationsUrl)
			);
		},

		machineKeysShouldRender(module) {
			const hasContainer = !!window.document.getElementById(
				'machine-keys-react-content',
			);
			const isCorrectUrl =
				window.location.href
					.toLowerCase()
					.indexOf('schemeserve/settings/edit_apikeys') !== -1;
			return (
				window.isFrontendEnabled &&
				hasContainer &&
				isCorrectUrl &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},

		usersShouldRender(module) {
			const isCorrectUrl =
				window.location.pathname.toLowerCase().search('adm/admusers.aspx') !==
				-1;

			const { search } = window.location;
			const params = new URLSearchParams(search);
			const isTestMode = params.get('testMode') === 'true';

			return (
				window.isFrontendEnabled &&
				isTestMode &&
				isCorrectUrl &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},

		permissionsShouldRender(module) {
			const hasContainer = !!window.document.getElementById(
				'permissions-react-content',
			);
			const isCorrectUrl =
				window.location.href
					.toLowerCase()
					.indexOf('schemeserve/admin/viewuser') !== -1;

			return (
				window.isFrontendEnabled &&
				// hasContainer &&
				(isCorrectUrl || process.env.NODE_ENV === 'development') &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},

		emailShouldRender(module) {
			return (
				window.isFrontendEnabled &&
				window.location.href
					.toLowerCase()
					.indexOf('schemeserve/admin/admin_emaillogs') !== -1 &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},

		cookiesBarShouldRender() {
			return window.isFrontendEnabled;
		},

		importMappingShouldRender() {
			return false;
		},

		fileLookUpsShouldRender(module) {
			const isCorrectUrl =
				window.location.href
					.toLowerCase()
					.indexOf('/schemeserve/admin/datalookups') !== -1;

			const { search } = window.location;
			const params = new URLSearchParams(search);
			const isTestMode = params.get('testMode') === 'true';
			return (
				isCorrectUrl &&
				isTestMode &&
				window.isFrontendEnabled &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},

		emailTemplatesShouldRender(module) {
			const isCorrectUrl =
				window.location.href
					.toLowerCase()
					.indexOf('/schemeserve/admin/emailtemplates.aspx') !== -1;

			const { search } = window.location;
			const params = new URLSearchParams(search);
			const isTestMode = params.get('testMode') === 'true';
			return (
				isCorrectUrl &&
				isTestMode &&
				window.isFrontendEnabled &&
				(window.isLoggedIn || !!module?.isPreLogin)
			);
		},
	};
}
